import { useState, createContext, useContext } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';

import {
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Typography,
	Box,
	Breadcrumbs,
	Link,
	CircularProgress,
	Alert,
} from '@mui/material';

import FolderIcon from '@mui/icons-material/FolderOutlined';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import FileIcon from '@mui/icons-material/InsertDriveFileOutlined';

import {
	Dialog,
	DialogTitle,
	DialogActions,
	DialogContent,
	DialogCloseButton,
	Button,
} from 'components/dialogs/DialogBox';

import ErrorAlert from 'components/ErrorAlert';

import { useSnackbar } from 'web/contexts/SnackbarContext';

import { GET_COMPANY_FOLDERS } from 'api/queries/documentQueries';
import { UPDATE_DOCUMENT_NODE } from 'api/mutations/documentMutations';
import { useDocumentSearchRefetchQueries } from 'web/components/fileBrowser/hooks/useDocumentSearch';

const MoveDocumentNodeModalContext = createContext({
	isOpen: false,
	documentNodeId: null,
});

export function useMoveDocumentNodeModal() {
	const context = useContext(MoveDocumentNodeModalContext);

	if (context === undefined) {
		throw new Error(
			'useMoveDocumentNodeModal must be used within a MoveDocumentNodeModalProvider'
		);
	}

	return context;
}

export default function MoveDocumentNodeModalProvider({ children }) {
	const [state, setState] = useState({ isOpen: false, documentNode: null });

	const value = {
		...state,
		move: documentNode => setState({ isOpen: true, documentNode }),
		close: () => setState({ isOpen: false, documentNode: null }),
	};

	return (
		<MoveDocumentNodeModalContext.Provider value={value}>
			{children}

			{state.isOpen && state.documentNode && (
				<MoveDocumentModal
					documentNode={state.documentNode}
					onClose={value.close}
				/>
			)}
		</MoveDocumentNodeModalContext.Provider>
	);
}

function MoveDocumentModal({ documentNode, onClose }) {
	const { notify } = useSnackbar();

	const [folderHistory, setFolderHistory] = useState([]);
	const [selectedFolderId, setSelectedFolderId] = useState('root');
	const [confirmedDestinationId, setConfirmedDestinationId] = useState(null);

	const {
		data: { folder: { currentFolder, subFolders } } = {
			folder: { currentFolder: null, subFolders: [] },
		},
		loading,
		error,
	} = useQuery(GET_COMPANY_FOLDERS, {
		variables: {
			_id: selectedFolderId,
			tenantId: documentNode.tenantId,
		},
	});

	const searchRefetchQueries = useDocumentSearchRefetchQueries();
	const [updateDocumentNode, { loading: updating }] = useMutation(
		UPDATE_DOCUMENT_NODE,
		{
			refetchQueries: () => [
				'paginateDocumentNodes',
				...searchRefetchQueries(),
			],
			onCompleted: () => {
				notify('Flytting gjennomført', 'success');
				onClose();
			},
			onError: error => {
				notify('Flytting feilet', 'error');
				console.error(error);
			},
		}
	);

	const handleClose = () => {
		setFolderHistory([]);
		setSelectedFolderId('root');
		setConfirmedDestinationId(null);
		onClose();
	};

	// A node marked in its own right keeps the mark wherever it lands; only an
	// inherited one is lost. Tied to the destination, so picking another one
	// asks again.
	const losesInheritedMark =
		!!documentNode?.isInternal &&
		!documentNode?.isMarkedInternal &&
		!currentFolder?.isInternal;
	const needsConfirmation =
		losesInheritedMark && confirmedDestinationId !== currentFolder?._id;

	const handleMoveFile = async () => {
		if (!currentFolder) return;

		if (needsConfirmation) {
			setConfirmedDestinationId(currentFolder._id);
			return;
		}

		try {
			await updateDocumentNode({
				variables: {
					_id: documentNode._id,
					parentId: currentFolder._id,
				},
			});

			handleClose();
		} catch (err) {
			console.error(err);
		}
	};

	const { name, type } = documentNode || {};
	const documentTypeName = type === 'document' ? 'dokument' : 'mappe';

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			fullWidth
			maxWidth="sm"
			aria-labelledby="move-document-modal-title"
			scroll="paper"
			slotProps={{
				paper: {
					sx: {
						position: 'fixed',
						top: 0,
					},
				},
			}}
		>
			<DialogTitle>
				<span id="move-document-modal-title">
					Flytt {documentTypeName}
				</span>
				<DialogCloseButton onClick={handleClose} disabled={updating} />
			</DialogTitle>

			<DialogContent dividers>
				<SelectedDocumentNode
					name={name}
					type={type}
					documentTypeName={documentTypeName}
				/>

				{error && <ErrorAlert error={error} />}

				<FolderList
					currentFolder={currentFolder}
					subFolders={subFolders}
					folderHistory={folderHistory}
					setFolderHistory={setFolderHistory}
					setSelectedFolderId={setSelectedFolderId}
					loading={loading}
				/>

				{losesInheritedMark && (
					<Alert severity="warning" sx={{ mt: 2 }}>
						«{name}» er intern fordi mappen den ligger i er merket
						intern. Flytter du den hit, blir den synlig for kunden.
					</Alert>
				)}
			</DialogContent>

			<DialogActions>
				<Button onClick={handleClose} disabled={updating}>
					Avbryt
				</Button>
				<Button
					onClick={handleMoveFile}
					variant="contained"
					color={needsConfirmation ? 'warning' : 'primary'}
					disabled={currentFolder === null}
					loading={updating}
				>
					{needsConfirmation
						? 'Bekreft flytting ut av intern mappe'
						: 'Flytt hit'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

function SelectedDocumentNode({ name, type, documentTypeName }) {
	return (
		<Box
			sx={{
				backgroundColor: 'action.hover',
				border: 1,
				borderColor: 'divider',
				mb: 2,
				py: 1,
				px: 2,
			}}
		>
			<Typography variant="body2" sx={{ color: 'text.light' }}>
				Valgt {documentTypeName}:
			</Typography>
			<Box
				sx={{
					py: 1,
					gap: 2,
					display: 'flex',
					alignItems: 'center',
				}}
			>
				{type === 'document' ? (
					<FileIcon color="primary" />
				) : (
					<FolderIcon color="primary" />
				)}

				<Typography variant="subtitle1">{name}</Typography>
			</Box>
		</Box>
	);
}

function FolderList({
	currentFolder,
	subFolders,
	folderHistory,
	setFolderHistory,
	setSelectedFolderId,
	loading,
}) {
	const noFolders = currentFolder === null && subFolders.length === 0;

	const handleFolderClick = id => {
		if (currentFolder?._id === id) {
			return;
		}

		if (currentFolder !== null) {
			setFolderHistory([...folderHistory, currentFolder]);
		}
		setSelectedFolderId(id);
	};

	if (loading) {
		return <CircularProgress />;
	}

	return (
		<>
			<Typography variant="subtitle2" sx={{ mb: 1 }}>
				Velg mappedestinasjon:
			</Typography>
			<Box
				sx={{
					backgroundColor: 'action.hover',
					border: 1,
					borderColor: 'divider',
					p: 1,
				}}
			>
				<Box sx={{ py: 1, px: 2 }}>
					<FolderBreadcrumbs
						folderHistory={folderHistory}
						currentFolder={currentFolder}
						setFolderHistory={setFolderHistory}
						setSelectedFolderId={setSelectedFolderId}
					/>
				</Box>

				<List>
					{currentFolder !== null && (
						<FolderListItem
							folder={currentFolder}
							currentFolder={currentFolder}
							onSelect={handleFolderClick}
						/>
					)}
					{subFolders?.map(folder => (
						<FolderListItem
							key={folder._id}
							folder={folder}
							currentFolder={currentFolder}
							onSelect={handleFolderClick}
						/>
					))}
					{noFolders && (
						<ListItem>
							<ListItemText
								primary="Ingen mapper tilgjengelig"
								primaryTypographyProps={{
									color: 'text.secondary',
									fontStyle: 'italic',
								}}
							/>
						</ListItem>
					)}
				</List>
			</Box>
		</>
	);
}

function FolderListItem({ folder, currentFolder, onSelect }) {
	const isCurrentFolder = folder._id === currentFolder?._id;

	return (
		<ListItem disablePadding>
			<ListItemButton
				onClick={() => onSelect(folder._id)}
				sx={{
					pl: !isCurrentFolder && currentFolder ? 3 : 2,
				}}
				selected={isCurrentFolder}
			>
				<ListItemIcon>
					{isCurrentFolder ? (
						<FolderOpenIcon color="primary" />
					) : (
						<FolderIcon color="action" />
					)}
				</ListItemIcon>
				<ListItemText
					primary={folder.name}
					primaryTypographyProps={{
						color: isCurrentFolder ? 'primary' : 'text.primary',
					}}
				/>
			</ListItemButton>
		</ListItem>
	);
}

function FolderBreadcrumbs({
	folderHistory,
	currentFolder,
	setFolderHistory,
	setSelectedFolderId,
}) {
	const isRoot = currentFolder === null;
	const currentPath = isRoot ? [] : [...folderHistory, currentFolder];

	const onSelect = ({ folderId, index }) => {
		if (folderId === 'root') {
			setFolderHistory([]);
		} else {
			setFolderHistory(currentPath.slice(0, index));
		}

		setSelectedFolderId(folderId);
	};

	return (
		<Breadcrumbs separator="›" aria-label="Brødsmuler" maxItems={3}>
			{isRoot ? (
				<FolderBreadcrumbItemText folderName="Dokumenter" />
			) : (
				<FolderBreadcrumbItemButton
					folder={{
						_id: 'root',
						name: 'Dokumenter',
					}}
					onSelect={onSelect}
					index={null}
				/>
			)}

			{currentPath.map((folder, index) => {
				const isLast = index === currentPath.length - 1;
				const { _id: fId, name: folderName } = folder;

				if (isLast) {
					return (
						<FolderBreadcrumbItemText
							key={fId}
							folderName={folderName}
						/>
					);
				} else {
					return (
						<FolderBreadcrumbItemButton
							key={fId}
							folder={folder}
							onSelect={onSelect}
							index={index}
						/>
					);
				}
			})}
		</Breadcrumbs>
	);
}

function FolderBreadcrumbItemText({ folderName }) {
	return (
		<Link
			variant="body2"
			component="button"
			color="text.primary"
			underline="none"
			disabled
			sx={{
				cursor: 'default',
			}}
		>
			{folderName}
		</Link>
	);
}

function FolderBreadcrumbItemButton({ folder, onSelect, index }) {
	const { _id: folderId, name: folderName } = folder;
	return (
		<Link
			variant="body2"
			component="button"
			underline="hover"
			color="inherit"
			onClick={() => {
				onSelect({ folderId, index });
			}}
		>
			{folderName}
		</Link>
	);
}
